/**
 * A child's code, in one tap: for the child who lost it, forgot it, or is on
 * a new tablet.
 *
 * The code was always recoverable -- it is sealed under the family key on the
 * child's row (`codeCt`) -- but only from Settings, four taps down, behind a
 * reveal toggle. It LASTS -- approving a device does not spend it -- so a
 * child can learn it, and a parent can print it (see RecoverySheet). This is
 * the quick way to it:
 *
 *   the code, big      to read out across the room
 *   a QR code          point the tablet's camera at it; it opens their page
 *                      with the code already filled in
 *   share, text, copy  to send the link to a phone that is somewhere else
 *
 * If there is no code yet -- the person is new, or the fresh one after an
 * approval failed to mint -- one is made as this opens. A parent who asked for
 * the code wants a code; making them press a second button for it is the slow
 * path this replaces.
 *
 * SHOWN PLAINLY, unlike the family code. On its own it gets nobody anything:
 * every device that types it produces a request a parent has to approve,
 * after checking a match code on the child's own screen.
 */

import { Loading } from '../components/Spinner';
import { useEffect, useState } from 'react';
import { mintChildCode, readChildCode } from '../family/devices';
import { format as formatCode } from '../family/codes';
import type { Person } from '../family/state';
import { ShareActions } from './ShareActions';
import { QrCode } from '../components/QrCode';
import { publicOrigin } from '../native/platform';

/** The link a child's device opens: their page, with the code filled in. */
export function childLink(code: string): string {
  return `${publicOrigin()}/me#code=${formatCode(code)}`;
}

export function ChildCode({
  familyId,
  familyKey,
  person,
}: {
  familyId: string;
  familyKey: CryptoKey;
  person: Person;
}) {
  /** `undefined` while reading, `null` if there is none and none could be made. */
  const [code, setCode] = useState<string | null | undefined>(undefined);
  const [working, setWorking] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState('');
  const name = person.name || 'They';

  function mint(): Promise<void> {
    setWorking(true);
    setError('');
    return mintChildCode(familyId, familyKey, person.id)
      .then(setCode)
      .catch((problem: unknown) => {
        setError(problem instanceof Error ? problem.message : 'A code could not be made.');
        setCode((current) => current ?? null);
      })
      .finally(() => setWorking(false));
  }

  useEffect(() => {
    let cancelled = false;
    void readChildCode(person, familyKey).then((found) => {
      if (cancelled) return;
      if (found) setCode(found);
      else void mint();
    });
    return () => {
      cancelled = true;
    };
    // Read once per person. `person` changes identity on every refresh, and
    // re-reading would flicker the code for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.id, person.codeCt, familyKey]);

  if (code === undefined) {
    return <Loading label={working ? 'Making a code…' : 'Finding their code…'} />;
  }
  if (code === null) {
    return (
      <div className="child-code">
        <p className="error">{error || 'There is no code yet.'}</p>
        <button aria-busy={working} type="button" disabled={working} onClick={() => void mint()}>
          {working ? 'Working…' : 'Try again'}
        </button>
      </div>
    );
  }

  const link = childLink(code);
  const subject = `${person.name ? `${person.name}’s` : 'Your'} Birbly page`;

  return (
    <div className="child-code">
      <div className="child-code-main">
        <QrCode text={link} label={`QR code that opens ${person.name || 'their'} page`} />
        <div className="child-code-text">
          <p className="field-label">{person.name ? `${person.name}’s code` : 'Their code'}</p>
          <p className="child-code-value">{formatCode(code)}</p>
          <p className="footnote">
            Point the tablet&rsquo;s camera at the square, or type the code at
            <strong> {new URL(publicOrigin()).host}/me</strong>. You approve the device
            when it asks.
          </p>
        </div>
      </div>

      <ShareActions
        code={formatCode(code)}
        familyName={person.name}
        link={link}
        subject={subject}
        message={`${subject}\n\n${link}\n\nOr open ${publicOrigin()}/me and type this code: ${formatCode(code)}\n`}
        text
      />

      <p className="footnote">
        It keeps working until you make a new one, so {name === 'They' ? 'they' : name} can
        learn it. Every device that uses it still needs your yes.
      </p>

      {error ? <p className="error">{error}</p> : null}
      {replacing ? (
        <div className="danger-box">
          <p>
            Make a new code? The one above stops working. Devices you have
            already approved keep working.
          </p>
          <div className="actions">
            <button aria-busy={working}
              type="button"
              className="primary"
              disabled={working}
              onClick={() => void mint().then(() => setReplacing(false))}
            >
              {working ? 'Working…' : 'Make a new code'}
            </button>
            <button type="button" disabled={working} onClick={() => setReplacing(false)}>
              Keep this one
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="text-button" onClick={() => setReplacing(true)}>
          Somebody else has it? Make a new code
        </button>
      )}
    </div>
  );
}
